import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError, } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import { OrderDirection } from "common/contracts/order-direction";
import PaginatedResponse from "common/contracts/paginated-response";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import _ from "lodash";
import { useSelector } from "react-redux";
import Session from "users/session/session";
import ProposalAPIService, { ProposalNotFoundError, } from "work/entities/proposal/api/proposal-api-service";
import ProposalParameters, {
  ProposalBoxType, ProposalContext,
  ProposalFilterParameters,
  ProposalOrderField,
  ProposalOrderParameters
} from "work/entities/proposal/api/request-contracts/proposal-parameters";
import BarebonesProposalInfoAPIResponse from "work/entities/proposal/api/response-contracts/barebones-proposal-info-api-response";
import Proposal from "work/entities/proposal/proposal";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";

type ProposalStoreState = {
  counts: {
    active: number | undefined;
    draft: number | undefined;
    archived: number | undefined;
    loading: boolean;
    error: SerializedError | null;
    
  }
  byId: {
    entries: Record<string, Proposal | null | undefined>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  builders: {
    entries: Record<string, ProposalBuilder | undefined>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  bareBones: {
    entries: Record<string, BarebonesProposalInfoAPIResponse | undefined>;
    loading: Record<string, boolean>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<Proposal>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  interfaceQueries: {
    entries: Record<string, ProposalQuery>;
  };
};
const initialState: ProposalStoreState = {
  counts: {
    active: undefined,
    draft: undefined,
    archived: undefined,
    loading: false,
    error: null
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  builders: {
    entries: {},
    loading: {},
    error: {},
  },
  bareBones: {
    entries: {},
    loading: {}
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export class ProposalQuery {
  constructor(
    public proposalParams: ProposalParameters,
    public pagination?: PaginationParameters
  ) {
  }

  public asSearchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.proposalParams.asSearchParams(),
      ...(this.pagination?.asSearchParams() ?? []),
    ]);
  }
}

export const populateProposalCounts = createAsyncThunk(
  "proposals/getProposalCounts",
  async ({ session }: { session: Session }, thunkAPI) => {
    const state = thunkAPI.getState() as { proposals: ProposalStoreState };
    const existing = state.proposals.counts;
    if (existing.active !== undefined && existing.draft !== undefined && existing.archived !== undefined) {
      return existing;
    }

    if (state.proposals.counts.loading) {
      throw new AlreadyPerformedError();
    }

    thunkAPI.dispatch(proposalsSlice.actions.setProposalCountsLoading(true));
    const workAgreementApiService = new WorkAgreementAPIService(session);
    const apiService = new ProposalAPIService(session, workAgreementApiService);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const context = session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client;

      const [activeCount, draftCount, archivedCount] = await Promise.all([
        loadActiveProposalCount(apiService, context, abortController),
        loadDraftProposalCount(apiService, context, abortController),
        loadArchivedProposalCount(apiService, context, abortController)
      ]);

      return {
        active: activeCount,
        draft: draftCount,
        archived: archivedCount
      };
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

const loadActiveProposalCount = async (
  proposalAPIService: ProposalAPIService, 
  context: ProposalContext,
  abortController: AbortController
): Promise<number> => {
    const query = new ProposalQuery(
      new ProposalParameters(
        new ProposalFilterParameters({
          boxType: ProposalBoxType.AllActive,
          context: context,
          isDeleted: false
        }),
        new ProposalOrderParameters(ProposalOrderField.Created, OrderDirection.Descending)
      ),
      new PaginationParameters(0, 0)
    );
    const response = await proposalAPIService.getProposals(query, abortController);
    return response.headers.totalElements;
};

const loadDraftProposalCount = async (
  proposalAPIService: ProposalAPIService, 
  context: ProposalContext, 
  abortController: AbortController
): Promise<number> => {
  const query = new ProposalQuery(
    new ProposalParameters(
      new ProposalFilterParameters({
        boxType: ProposalBoxType.Draft,
        context: context,
        isDeleted: false
      }),
      new ProposalOrderParameters(ProposalOrderField.Created, OrderDirection.Descending)
    ),
    new PaginationParameters(0, 0)
  );
  const response = await proposalAPIService.getProposals(query, abortController);
  return response.headers.totalElements;
};

const loadArchivedProposalCount = async (
  proposalAPIService: ProposalAPIService, 
  context: ProposalContext, 
  abortController: AbortController
): Promise<number> => {
  const query = new ProposalQuery(
    new ProposalParameters(
      new ProposalFilterParameters({
        boxType: ProposalBoxType.Archive,
        context: context,
        isDeleted: false
      }),
      new ProposalOrderParameters(ProposalOrderField.Created, OrderDirection.Descending)
    ),
    new PaginationParameters(0, 0)
  );
  const response = await proposalAPIService.getProposals(query, abortController);
  return response.headers.totalElements;
};


export const populateProposal = createAsyncThunk(
  "proposals/getProposalById",
  async (
    {
      session,
      id
    }: { session: Session; id: Guid; },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as { proposals: ProposalStoreState };
    const existing = state.proposals.byId.entries[id.value];
    if (existing || state.proposals.byId.loading[id.value]) {
      throw new AlreadyPerformedError();
    }

    thunkAPI.dispatch(proposalsSlice.actions.setProposalLoading({ id, isLoading: true }));
    try {
      const workAgreementApiService = new WorkAgreementAPIService(session);
      const apiService = new ProposalAPIService(session, workAgreementApiService);
      return await apiService.getProposalById(id);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

class AlreadyPerformedError extends Error {
  public static defaultMessage = "Already performed";
  constructor(message?: string) {
    super(message ?? AlreadyPerformedError.defaultMessage);
    this.name = "AlreadyPerformedError";
  }
}
export const populateProposalsByQuery = createAsyncThunk(
  "proposals/getProposalsByQuery",
  async (
    { session, query }: { session: Session; query: ProposalQuery },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as { proposals: ProposalStoreState };

    const existing = state.proposals.byQuery.entries[query.asSearchParams().toString()];
    if (existing) {
      return existing;
    }

    if (state.proposals.byQuery.loading[query.asSearchParams().toString()]) {
      throw new AlreadyPerformedError();
    }

    thunkAPI.dispatch(proposalsSlice.actions.setProposalsLoading({ query: query, isLoading: true }));
    const workAgreementApiService = new WorkAgreementAPIService(session);
    const apiService = new ProposalAPIService(session, workAgreementApiService);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getProposals(query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateBarebonesProposals = createAsyncThunk<
  BarebonesProposalInfoAPIResponse[],
  { session: Session; proposalIds: Guid[] }
>(
  "proposals/populateBarebonesProposals",
  async ({ session, proposalIds }: { session: Session; proposalIds: Guid[] }, thunkAPI) => {
    if (!proposalIds.length) return [];

    const store: { proposals: ProposalStoreState } = thunkAPI.getState() as { proposals: ProposalStoreState };
    const missingProposalsIds = _.uniqWith(
      proposalIds.filter((proposalId) => !store.proposals.bareBones.entries[proposalId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingProposalsIds.length) return [];

    const apiService = new ProposalAPIService(session, new WorkAgreementAPIService(session));
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getProposalsByIds(proposalIds, abortController);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);


const proposalsSlice = createSlice({
  name: "proposals",
  initialState,
  reducers: {
    addProposal: (state, action: PayloadAction<Proposal>) => {
      const proposal = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }

      state.byId.entries[proposal.id.value] = proposal;
      state.counts = {
        draft: undefined,
        active: undefined,
        archived: undefined,
        loading: false,
        error: null
      }
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };

    },
    updateProposal: (state, action: PayloadAction<Proposal>) => {
      const proposal = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }
      state.byId.entries[proposal.id.value] = proposal;
    },
    removeProposal: (state, action: PayloadAction<Guid>) => {
      state.byId.entries[action.payload.value] = null;
      state.counts = {
        draft: undefined,
        active: undefined,
        archived: undefined,
        loading: false,
        error: null
      }
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    addProposalBuilder: (
      state,
      action: PayloadAction<{ proposalId: Guid; builder: ProposalBuilder }>
    ) => {
      if (
        !action.payload.proposalId ||
        state.builders.entries[action.payload.proposalId.value]
      ) {
        return;
      }
      const proposalId = action.payload.proposalId.value;
      state.builders.entries[proposalId] = action.payload.builder;
    },
    clearNewProposalBuilder: (state) => {
      state.builders.entries[Guid.empty.toString()] = undefined;
    },
    replaceProposalBuilder: (
      state,
      action: PayloadAction<{ proposalId: Guid; builder: ProposalBuilder }>
    ) => {
      const proposalId = action.payload.proposalId.value;
      state.builders.entries[proposalId] = action.payload.builder;
    },
    resetBuilderByProposalId: (state, action: PayloadAction<Guid>) => {
      const builder = state.builders.entries[action.payload.value];
      if (builder) {
        state.builders.entries[action.payload.value] = builder.resetSession();
      }
    },
    updateProposalInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ProposalQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
    resetProposalSessionByProposalId: (state, action: PayloadAction<Guid>) => {
      const builder: ProposalBuilder = state.builders.entries[action.payload.value] as ProposalBuilder;
      if (!builder) {
        return;
      }
      state.builders.entries[action.payload.value] = builder.resetSession();
    },
    updateAllQueriesLoading: (state, action: PayloadAction<boolean>) => {
      for (const [query] of Object.entries(state.byQuery.loading)) {
        state.byQuery.loading[query] = action.payload;
      }
    },
    setProposalCountsLoading: (state, action: PayloadAction<boolean>) => {
      state.counts.loading = action.payload;
    },
    setProposalLoading: (state, action: PayloadAction<{ id: Guid; isLoading: boolean }>) => {
      state.byId.loading[action.payload.id.value] = action.payload.isLoading;
    },
    setProposalsLoading: (state, action: PayloadAction<{ query: ProposalQuery; isLoading: boolean }>) => {
      state.byQuery.loading[action.payload.query.asSearchParams().toString()] = action.payload.isLoading;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(populateProposalCounts.pending, (state) => {
      state.counts.error = null;
    });

    builder.addCase(
      populateProposalCounts.fulfilled,
      (state, action) => {
        if (action.payload === undefined || action.payload === null) {
          return;
        }
        state.counts.active = action.payload.active;
        state.counts.draft = action.payload.draft;
        state.counts.archived = action.payload.archived;
        state.counts.loading = false;
        state.counts.error = null;
      }
    );
    builder.addCase(populateProposalCounts.rejected, (state, action) => {
      if (action.error instanceof AlreadyPerformedError || action.error.message === AlreadyPerformedError.defaultMessage) {
        return;
      }
      state.counts.loading = false;
      state.counts.error = action.error;
    });    
    builder.addCase(
      populateProposal.pending,
      (state, action) => {
        const proposalId = action.meta.arg.id.value;
        state.byId.error[proposalId] = null;
      }
    );
    builder.addCase(
      populateProposal.fulfilled,
      (state, action) => {
        const proposal = action.payload.proposal;
        const userProposalBuilder = action.payload.userProposalBuilder;
        if (!proposal.id) {
          console.error(
            "Proposal does not have an id",
            proposal
          );
          return;
        }
        state.byId.entries[proposal.id.value] = proposal; 
        state.byId.loading[proposal.id.value] = false;
        state.byId.error[proposal.id.value] = null;
        state.builders.entries[proposal.id.value] = userProposalBuilder;
        state.builders.error[proposal.id.value] = null;
        state.builders.loading[proposal.id.value] = false;
      }
    );
    builder.addCase(
      populateProposal.rejected,
      (state, action) => {
        if (action.error instanceof AlreadyPerformedError || action.error.message === AlreadyPerformedError.defaultMessage) {
          return;
        }
        const proposalId = action.meta.arg.id.value;
        state.byId.loading[proposalId] = false;
        state.byId.error[proposalId] = action.error;
        if (action.error instanceof ProposalNotFoundError) {
          state.byId.entries[proposalId] = null;
        }
      }
    );

    builder.addCase(
      populateProposalsByQuery.pending,
      (state, action) => {
        if (!action.meta.arg.query?.proposalParams) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populateProposalsByQuery.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query?.proposalParams) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = false;
        state.byQuery.entries[query] = action.payload;
        state.byQuery.error[query] = null;        
      }
    );
    builder.addCase(
      populateProposalsByQuery.rejected,
      (state, action) => {
        if (!action.meta.arg.query?.proposalParams) {
          return;
        }
        if (action.error instanceof AlreadyPerformedError || action.error.message === AlreadyPerformedError.defaultMessage) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.error;
      }
    );

    builder.addCase(
      populateBarebonesProposals.pending,
      (state, action) => {
        if (!action.meta.arg.proposalIds?.length) return;
        for (const proposalId of action.meta.arg.proposalIds) {
          state.bareBones.loading[proposalId.value] = true;
        }
      }
    );
    builder.addCase(
      populateBarebonesProposals.fulfilled,
      (state, action) => {
        const requestedIds = action.meta.arg.proposalIds.map(id => id.value);
        const returnedIds = new Set((action.payload ?? []).map(p => p.id));

        for (const proposal of action.payload) {
          if (!proposal.id) {
            console.error("Proposal does not have an id", proposal);
            continue;
          }
          state.bareBones.entries[proposal.id] = proposal;
          state.bareBones.loading[proposal.id] = false;
        }

        for (const id of requestedIds) {
          if (!returnedIds.has(id)) {
            state.bareBones.loading[id] = false;
          }
        }
      }
    );
  }
});

export const {
  addProposal,
  updateProposal,
  removeProposal,
  addProposalBuilder,
  clearNewProposalBuilder,
  replaceProposalBuilder,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery,
  updateAllQueriesLoading
} = proposalsSlice.actions;
export const getProposalById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.proposals?.byId.entries[id.value];
  });
export const getIsLoadingProposalById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return false;
    return (
      state.proposals?.byId.loading[id.value] || state.proposals?.bareBones.loading[id.value]
    );
  });
export const getErrorLoadingProposalById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.proposals?.byId.error[id.value] || state.proposals?.bareBones.entries[id.value]?.error;
  });
export const getProposalBuilderByProposalId = (proposalId?: Guid | null) =>
  useSelector((state: RootState) => {
    if (!proposalId) return undefined;
    return state.proposals?.builders.entries[proposalId.value];
  });
export const getProposalQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.proposals.interfaceQueries.entries[interfaceName]
  );
export const getProposalsByQuery = (query: ProposalQuery): PaginatedResponse<Proposal> | undefined =>
  useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    const searchParams = query.asSearchParams().toString();
    return state.proposals?.byQuery.entries[searchParams];
  });
export const getIsLoadingProposalsByQuery = (query: ProposalQuery) =>
  useSelector((state: RootState) => {
    if (!query) {
      return false;
    }
    const searchParams = query.asSearchParams().toString();
    return state.proposals?.byQuery.loading[searchParams];
  });
export const getErrorLoadingProposalsByQuery = (query: ProposalQuery) =>
  useSelector((state: RootState) => {
    if (!query) {
      return false;
    }
    const searchParams = query.asSearchParams().toString();
    return state.proposals?.byQuery.error[searchParams];
  });


export const getProposalsByIds: (ids: Guid[]) => BarebonesProposalInfoAPIResponse[] = (ids: Guid[]) =>
  useSelector(
    createSelector(
      (state: RootState) => state.proposals.bareBones.entries,
      (entries) => ids
        .map(id => entries[id.toString()])
        .filter(proposal => proposal !== undefined)
    )
  );
export const getLoadingProposalIds = () => {
  const state = useSelector((state: RootState) => state.proposals);
  const detailedIds = Object
    .keys(state.byId.loading)
    .filter((proposalId) => state.byId.loading[proposalId])
    .map((proposalId) => Guid.fromJSON(proposalId));

  const bareBonesIds = Object
    .keys(state.bareBones.loading)
    .filter((proposalId) => state.bareBones.loading[proposalId])
    .map((proposalId) => Guid.fromJSON(proposalId));

  return [...detailedIds, ...bareBonesIds];
}
export type ProposalError = {
  id: Guid;
  error: SerializedError;
}
export const getActiveProposalCount = () =>
  useSelector((state: RootState) => state.proposals.counts.active);
export const getDraftProposalCount = () =>
  useSelector((state: RootState) => state.proposals.counts.draft);
export const getArchivedProposalCount = () =>
  useSelector((state: RootState) => state.proposals.counts.archived);
export const getIsLoadingProposalCounts = () =>
  useSelector((state: RootState) => state.proposals.counts.loading);

export const getErrorLoadingProposalCounts = () =>
  useSelector((state: RootState) => state.proposals.counts.error);

export const getAreErrorsLoadingProposalsByIds = (ids: Guid[]) =>
  useSelector(
    (state: RootState) => ids.some(id => state.proposals.byId.error[id.toString()] || state.proposals.bareBones.entries[id.toString()]?.error)
  );
export const getAllProposalErrors = (): ProposalError[] => {
  const state = useSelector((state: RootState) => state.proposals);

  const detailedErrors = Object.keys(state.byId.error)
    .map((proposalId) => {
      const error = state.byId.error[proposalId];
      if (!error?.message) return null;
      const id = Guid.fromJSON(proposalId);
      if (!id) return null;
      return { id, error };
    })
    .filter(Boolean) as ProposalError[];

  const bareBonesErrors = Object.keys(state.bareBones.entries).map((proposalId) => {
    const error = state.bareBones.entries[proposalId]?.error;
    if (!error) return null;
    const id = Guid.fromJSON(proposalId);
    return { id, error };
  }).filter(Boolean) as ProposalError[];

  return [...detailedErrors, ...bareBonesErrors];
};

export default proposalsSlice;
